import React, { useEffect, useMemo, useState } from 'react';
import styles from './RelatedCompaniesNetwork.module.scss';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { hollowBlackInfoIcon, ctaPurpleRight } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { formatDateByFormat } from '../../../../../../utils/dateUtils';
import { DATE_FORMAT, RELATED_COMPANIES_NETWORK_TOOLTIP, VIEW_CONFIGS } from '../../../../../../utils/constants/commonConstants';
import { useAppSelector } from '../../../../../../app/hooks';
import clsx from 'clsx';
import RelatedCompaniesNetworkDrawer from './RelatedCompaniesNetworkDrawer';
import { CompaniesNetworkData, StatusOption } from './types';
import { getDealFormConfig } from '../../../../../../store/investor/apiHelpers';

interface RelatedCompaniesNetworkProps {
    data: CompaniesNetworkData | null;
    isLoading?: boolean;
    isPDFReport?: boolean;
}

const RelatedCompaniesNetwork: React.FC<RelatedCompaniesNetworkProps> = ({ data, isLoading = false, isPDFReport = false }) => {
    const effectiveData: CompaniesNetworkData = data || { vendorFor: [], customerOf: [], lastUpdatedAt: undefined };

    const vendorCount = effectiveData.vendorFor?.length || 0;
    const customerCount = effectiveData.customerOf?.length || 0;
    const hasData = !!(data && (vendorCount || customerCount));
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localStatusOptions, setLocalStatusOptions] = useState<Record<string, StatusOption>>({});
    const investorConfig = useAppSelector((state) => state.investor.config);
    const userData = useAppSelector((state) => state.user.userData);

    const extractCompanyStatusOptions = (config?: any): Record<string, StatusOption> => {
        const columns = config?.leadsColumns?.columns || [];
        const statusColumn = columns.find((col: any) => col?.field === 'companyStatus');
        return statusColumn?.dropdownOptions || {};
    };

    const configStatusOptions = extractCompanyStatusOptions(investorConfig?.additionalDetailsConfig);

    const statusOptionsSource = Object.keys(localStatusOptions).length > 0 ? localStatusOptions : configStatusOptions;

    const statusOptions = useMemo<StatusOption[]>(() => Object.values(statusOptionsSource || {}), [statusOptionsSource]);

    useEffect(() => {
        const hasStatusOptions = Object.keys(statusOptionsSource || {}).length > 0;
        if (isPDFReport || isLoading || hasStatusOptions) return;
        if (!userData?.accountType || !userData?.investorOrgId || !userData?.primaryEmail) return;

        getDealFormConfig(
            {
                accountType: userData.accountType,
                investorOrgId: userData.investorOrgId,
                primaryEmail: userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (additionalDetailsConfig) => {
                    const dropdowns = extractCompanyStatusOptions(additionalDetailsConfig);
                    setLocalStatusOptions(dropdowns);
                },
            },
        );
    }, [isPDFReport, isLoading, statusOptionsSource, userData?.accountType, userData?.investorOrgId, userData?.primaryEmail]);

    if (!isLoading && !hasData) {
        return null;
    }

    const getStatusMeta = (status?: string): StatusOption | null => {
        if (!status) return null;
        return statusOptions.find((option) => option?.value?.toLowerCase?.() === status?.toLowerCase?.()) || null;
    };

    const renderTable = () => {
        // When loading, don't show anything
        if (isLoading) {
            return null;
        }

        // When data is available, show full table
        return (
            <div className={styles.TableContainer}>
                <table className={styles.Table}>
                    <tbody className={styles.TableBody}>
                        <tr className={styles.SectionHeaderRow}>
                            <td className={styles.SectionHeader} colSpan={1}>
                                Vendors for ({vendorCount})
                            </td>
                        </tr>
                        <tr className={styles.SectionHeaderRow}>
                            <td className={styles.SectionHeader} colSpan={1}>
                                Customers for ({customerCount})
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const headerRight = isLoading ? (
        <div className={styles.LoaderMessage}>
            <LoadingSpinner color="#6021b3" height="15px" />
            <span>Fetching Data</span>
        </div>
    ) : data?.lastUpdatedAt ? (
        <div className={styles.LastUpdated}>Last Updated on {formatDateByFormat(data.lastUpdatedAt, DATE_FORMAT)}</div>
    ) : null;

    const title = (
        <div className={styles.TitleContainer}>
            <span>Related Entities in Network</span>
            <Tooltip content={RELATED_COMPANIES_NETWORK_TOOLTIP} direction="top" usePortal={true}>
                <img src={hollowBlackInfoIcon} alt="info" className={styles.InfoIcon} />
            </Tooltip>
        </div>
    );

    return (
        <HeaderBodyBox title={title} headerRight={headerRight} className={styles.RelatedCompaniesNetwork} isPDFReport={isPDFReport}>
            {(isLoading || hasData) && (
                <div className={styles.CardBody}>
                    {renderTable()}
                    {!isPDFReport && !isLoading && (
                        <button type="button" className={styles.ViewDetailsButton} onClick={() => setIsDrawerOpen(true)}>
                            <img src={ctaPurpleRight} alt="" />
                            View Detailed Information
                        </button>
                    )}
                    {!isPDFReport && isDrawerOpen && (
                        <RelatedCompaniesNetworkDrawer
                            data={effectiveData}
                            onClose={() => setIsDrawerOpen(false)}
                            statusOptions={statusOptions}
                        />
                    )}
                </div>
            )}
        </HeaderBodyBox>
    );
};

export default RelatedCompaniesNetwork;
